// cloneDeep is currently not replaceable with either remeda or structuredClone
// because of JSDOM issues
// import { cloneDeep } from 'lodash';
// This is the only place in this stack where we use `ElementApi` directly! Use this file in all other places.
// oxlint-disable-next-line no-restricted-imports
import { ElementApi as PlateElementApi, NodeApi, type TNode } from 'platejs';
import { isEmpty, isString } from 'remeda';

import { type BlueskyElement, PlateBluesky } from '@@plate/serialization/unity/elements/Bluesky';
import { type CaptionElement, Caption } from '@@plate/serialization/unity/elements/Caption';
import { Credit } from '@@plate/serialization/unity/elements/Credit';
import {
    ANY_CROSSHEAD_ELEMENT_TYPES,
    type AnyCrossheadElement,
    CROSSHEAD_SUBSECTION_ELEMENT_TYPES,
    type CrossheadDefaultElement,
    type CrossheadOrderedListicleElement,
    type CrossheadSubsectionElement,
    type CrossheadUnorderedListicleElement,
    PlateAnyCrosshead,
    PlateCrossheadDefault,
    PlateCrossheadOrderedListicle,
    PlateCrossheadSubsection,
    PlateCrossheadUnorderedListicle,
} from '@@plate/serialization/unity/elements/Crosshead';
import {
    type CustomJsonElement,
    PlateCustomJson,
} from '@@plate/serialization/unity/elements/CustomJson';
import {
    DYNAMIC_TEASER_TITLE_ELEMENT_TYPES,
    type DynamicTeaserElement,
    type DynamicTeaserTitleElement,
    PlateDynamicTeaser,
    PlateDynamicTeaserTitle,
} from '@@plate/serialization/unity/elements/DynamicTeaser';
import {
    type EmbeddedContentElement,
    PlateEmbeddedContent,
} from '@@plate/serialization/unity/elements/EmbeddedContent';
import { type FacebookElement, PlateFacebook } from '@@plate/serialization/unity/elements/Facebook';
import {
    FOOTER_ELEMENT_TYPES,
    type FooterElement,
    PlateFooter,
} from '@@plate/serialization/unity/elements/Footer';
import {
    type FrontendComponentElement,
    PlateFrontendComponent,
} from '@@plate/serialization/unity/elements/FrontendComponent';
import { type ImageElement, PlateImage } from '@@plate/serialization/unity/elements/Image';
import {
    INFOBOX_CONTENT_ELEMENT_TYPES,
    INFOBOX_ELEMENT_TYPES,
    type InfoboxContentElement,
    type InfoboxElement,
    type InfoboxTitleElement,
    PlateInfobox,
    PlateInfoboxContent,
    PlateInfoboxContentElement,
    type PlateInfoboxContentElement as PlateInfoboxContentElementType,
    PlateInfoboxTitle,
} from '@@plate/serialization/unity/elements/Infobox';
import {
    type InstagramElement,
    PlateInstagram,
} from '@@plate/serialization/unity/elements/Instagram';
import {
    INTERVIEW_SEGMENT_ELEMENT_TYPES,
    type InterviewSegmentAnswerElement,
    type InterviewSegmentElement,
    type InterviewSegmentQuestionElement,
    PlateInterviewSegment,
    PlateInterviewSegmentAnswer,
    PlateInterviewSegmentQuestion,
} from '@@plate/serialization/unity/elements/InterviewSegment';
import { type LeadElement, PlateLead } from '@@plate/serialization/unity/elements/Lead';
import {
    type AuthorLinkElement,
    type CategoryLinkElement,
    type ExternalLinkElement,
    type InternalLinkElement,
    LINK_ELEMENT_TYPES,
    type LinkElement,
    PlateAuthorLink,
    PlateCategoryLink,
    PlateExternalLink,
    PlateInternalLink,
    PlateLink,
    PlateTagLink,
    type TagLinkElement,
} from '@@plate/serialization/unity/elements/Link';
import {
    type ListElement,
    type ListItemElement,
    type OrderedListElement,
    PlateListItem,
    PlateOrderedList,
    PlateUnorderedList,
    type UnorderedListElement,
} from '@@plate/serialization/unity/elements/List';
import {
    PARAGRAPH_ELEMENT_TYPES,
    type ParagraphElement,
    PlateParagraph,
} from '@@plate/serialization/unity/elements/Paragraph';
import {
    PlatePoll,
    PlatePollAnswer,
    PlatePollQuestion,
    type PollAnswerElement,
    type PollElement,
    type PollQuestionElement,
} from '@@plate/serialization/unity/elements/Poll';
import {
    PlateQuote,
    PlateQuoteCaption,
    PlateQuoteText,
    QUOTE_CAPTION_ELEMENT_TYPES,
    QUOTE_TEXT_ELEMENT_TYPES,
    type QuoteCaptionElement,
    type QuoteElement,
    type QuoteTextElement,
} from '@@plate/serialization/unity/elements/Quote';
import {
    PlateRatingPoll,
    type RatingPollElement,
} from '@@plate/serialization/unity/elements/RatingPoll';
import {
    PlateSeparator,
    type SeparatorElement,
} from '@@plate/serialization/unity/elements/Separator';
import {
    type CsSlideshowElement,
    PlateCsSlideshow,
    PlateSlideshow,
    type SlideshowElement,
} from '@@plate/serialization/unity/elements/Slideshow';
import {
    type EmbeddedSnippetElement,
    PlateEmbeddedSnippet,
    PlateSnippet,
    type SnippetElement,
} from '@@plate/serialization/unity/elements/Snippet';
import {
    type SummaryListElement,
    type SummaryListSummaryElement,
    PlateSummaryList,
    PlateSummaryListSummary,
} from '@@plate/serialization/unity/elements/Summary';
import { PlateThreads, type ThreadsElement } from '@@plate/serialization/unity/elements/Threads';
import { PlateTiktok, type TiktokElement } from '@@plate/serialization/unity/elements/Tiktok';
import { PlateTitle, type TitleElement } from '@@plate/serialization/unity/elements/Title';
import {
    PlateTitleHeader,
    type TitleHeaderElement,
} from '@@plate/serialization/unity/elements/TitleHeader';
import { PlateTwitter, type TwitterElement } from '@@plate/serialization/unity/elements/Twitter';
import { PlateVideoCms, type VideoCmsElement } from '@@plate/serialization/unity/elements/VideoCms';
import { PlateYoutube, type YoutubeElement } from '@@plate/serialization/unity/elements/Youtube';
import { PlateZattoo, type ZattooElement } from '@@plate/serialization/unity/elements/Zattoo';
import {
    BLOCK_ELEMENT_TYPES,
    type BlockElement,
    type InlineElement,
    NON_LISTABLE_ELEMENT_TYPES,
    PlateBlockElement,
    PlateInlineElement,
    PlateTextBlockElement,
    PlateTextElement,
    TEXT_BLOCK_ELEMENT_TYPES,
    TEXT_HEADING_ELEMENT_TYPES,
    type TextBlockElement,
    type TextElement,
} from '@@plate/serialization/unity/groups';

import type CreditElement from '@@plate/plugins/components/CreditElement';

interface ElementApi {
    isBlockList: (value: unknown) => value is BlockElement[];
    isBlockElement: (value: unknown) => value is BlockElement;
    isBlockElementType: (node: TNode) => boolean;
    isInlineElement: (value: unknown) => value is InlineElement;
    isLinkElement: (value: unknown) => value is LinkElement;
    isLinkElementType: (node: TNode) => boolean;
    isExternalLinkElement: (value: unknown) => value is ExternalLinkElement;
    isInternalLinkElement: (value: unknown) => value is InternalLinkElement;
    isTagLinkElement: (value: unknown) => value is TagLinkElement;
    isCategoryLinkElement: (value: unknown) => value is CategoryLinkElement;
    isAuthorLinkElement: (value: unknown) => value is AuthorLinkElement;
    isOrderedListElement: (value: unknown) => value is OrderedListElement;
    isUnorderedListElement: (value: unknown) => value is UnorderedListElement;
    isListElement: (value: unknown) => value is ListElement;
    isListItemElement: (value: unknown) => value is ListItemElement;
    isNonListableElementType: (node: TNode) => boolean;
    isAnyCrossheadElement: (value: unknown) => value is AnyCrossheadElement;
    isAnyCrossheadElementType: (node: TNode) => boolean;
    isCrossheadDefaultElement: (value: unknown) => value is CrossheadDefaultElement;
    isCrossheadSubsectionElement: (value: unknown) => value is CrossheadSubsectionElement;
    isCrossheadSubsectionElementType: (node: TNode) => boolean;
    isCrossheadOrderedListicleElement: (value: unknown) => value is CrossheadOrderedListicleElement;
    isCrossheadUnorderedListicleElement: (
        value: unknown,
    ) => value is CrossheadUnorderedListicleElement;
    isFooterElement: (value: unknown) => value is FooterElement;
    isFooterElementType: (node: TNode) => boolean;
    isParagraphElement: (value: unknown) => value is ParagraphElement;
    isParagraphElementType: (node: TNode) => boolean;
    isTitleElement: (value: unknown) => value is TitleElement;
    isTitleHeaderElement: (value: unknown) => value is TitleHeaderElement;
    isLeadElement: (value: unknown) => value is LeadElement;
    isEmptyParagraphElement: (value: unknown) => boolean;
    isBlueskyElement: (value: unknown) => value is BlueskyElement;
    isFacebookElement: (value: unknown) => value is FacebookElement;
    isTwitterElement: (value: unknown) => value is TwitterElement;
    isYoutubeElement: (value: unknown) => value is YoutubeElement;
    isInstagramElement: (value: unknown) => value is InstagramElement;
    isThreadsElement: (value: unknown) => value is ThreadsElement;
    isTiktokElement: (value: unknown) => value is TiktokElement;
    isZattooElement: (value: unknown) => value is ZattooElement;
    isFrontendComponentElement: (value: unknown) => value is FrontendComponentElement;
    isCustomJsonElement: (value: unknown) => value is CustomJsonElement;
    isSlideshowElement: (value: unknown) => value is SlideshowElement;
    isCsSlideshowElement: (value: unknown) => value is CsSlideshowElement;
    isPollElement: (value: unknown) => value is PollElement;
    isPollQuestionElement: (value: unknown) => value is PollQuestionElement;
    isPollAnswerElement: (value: unknown) => value is PollAnswerElement;
    isRatingPollElement: (value: unknown) => value is RatingPollElement;
    isVideoCmsElement: (value: unknown) => value is VideoCmsElement;
    isEmptyPollQuestionElement: (value: unknown) => boolean;
    isEmptyPollAnswerElement: (value: unknown) => boolean;
    isSnippetElement: (value: unknown) => value is SnippetElement;
    isEmbeddedSnippetElement: (value: unknown) => value is EmbeddedSnippetElement;
    isSeparatorElement: (value: unknown) => value is SeparatorElement;
    isInterviewSegmentElement: (value: unknown) => value is InterviewSegmentElement;
    isInterviewSegmentElementType: (node: TNode) => boolean;
    isInterviewSegmentQuestionElement: (value: unknown) => value is InterviewSegmentQuestionElement;
    isInterviewSegmentAnswerElement: (value: unknown) => value is InterviewSegmentAnswerElement;
    isEmptyInterviewSegmentQuestionElement: (value: unknown) => boolean;
    isEmptyInterviewSegmentAnswerElement: (value: unknown) => boolean;
    isEmptyInterviewSegmentElement: (value: unknown) => boolean;
    isEmptyInfoboxTitleElement: (value: unknown) => boolean;
    isEmptyInfoboxContentElement: (value: unknown) => boolean;
    isQuoteElement: (value: unknown) => value is QuoteElement;
    isQuoteTextElement: (value: unknown) => value is QuoteTextElement;
    isQuoteTextElementType: (node: TNode) => boolean;
    isQuoteCaptionElement: (value: unknown) => value is QuoteCaptionElement;
    isQuoteCaptionElementType: (node: TNode) => boolean;
    isEmptyQuoteElement: (value: unknown) => boolean;
    isEmptyQuoteTextElement: (value: unknown) => boolean;
    isEmptyQuoteCaptionElement: (value: unknown) => boolean;
    isTextElement: (value: unknown) => value is TextElement;
    isEmptyTextElement: (value: unknown) => boolean;
    isTextBlockElement: (value: unknown) => value is TextBlockElement;
    isTextBlockElementType: (node: TNode) => boolean;
    isTextHeadingElement: (
        value: unknown,
    ) => value is TitleHeaderElement | TitleElement | LeadElement;
    isTextHeadingElementType: (node: TNode) => boolean;
    isImageElement: (value: unknown) => value is ImageElement;
    isEmptyImageElement: (value: unknown) => boolean;
    isDynamicTeaserElement: (value: unknown) => value is DynamicTeaserElement;
    isEmptyDynamicTeaserElement: (value: unknown) => boolean;
    isDynamicTeaserTitleElement: (value: unknown) => value is DynamicTeaserTitleElement;
    isDynamicTeaserTitleElementType: (node: TNode) => boolean;
    isEmptyDynamicTeaserTitleElement: (value: unknown) => boolean;
    isCaptionElement: (value: unknown) => value is CaptionElement;
    isCreditElement: (value: unknown) => value is CreditElement;
    isEmptyCaptionElement: (value: unknown) => boolean;
    isEmptyCreditElement: (value: unknown) => boolean;
    isInfoboxElement: (value: unknown) => value is InfoboxElement;
    isInfoboxElementType: (node: TNode) => boolean;
    isInfoboxTitleElement: (value: unknown) => value is InfoboxTitleElement;
    isInfoboxContentElement: (value: unknown) => value is InfoboxContentElement;
    isInfoboxContentElementType: (node: TNode) => boolean;
    isInfoboxContentElementChild: (value: unknown) => value is PlateInfoboxContentElementType;
    isEmptyInfoboxElement: (value: unknown) => boolean;
    isEmbeddedContentElement: (value: unknown) => value is EmbeddedContentElement;
    isEmptyEmbeddedContentElement: (value: unknown) => boolean;
    isEmptySocialEmbedElement: (value: unknown) => boolean;
    isSummaryListElement: (value: unknown) => value is SummaryListElement;
    isSummaryListSummaryElement: (value: unknown) => value is SummaryListSummaryElement;
    isEmptySummaryListElement: (value: unknown) => value is SummaryListElement;
    isEmptySummaryListSummaryElement: (value: unknown) => value is SummaryListSummaryElement;
    // Issue #4120
    // oxlint-disable-next-line typescript/no-explicit-any
    isEmptyElement: (value: any) => boolean;
    // Issue #4120
    // oxlint-disable-next-line typescript/no-explicit-any
    isTemplateElement: (value: any) => boolean;
}

export const ElementApi: typeof PlateElementApi & ElementApi = {
    ...PlateElementApi,

    isBlockList: (value) =>
        ElementApi.isElementList(value) && value.every(ElementApi.isBlockElement),

    isBlockElement: (value): value is BlockElement => PlateBlockElement.safeParse(value).success,

    isBlockElementType: (node) => isString(node.type) && BLOCK_ELEMENT_TYPES.has(node.type),

    isInlineElement: (value): value is InlineElement => PlateInlineElement.safeParse(value).success,

    isLinkElement: (value): value is LinkElement => PlateLink.safeParse(value).success,

    isLinkElementType: (node) => isString(node.type) && LINK_ELEMENT_TYPES.has(node.type),

    isExternalLinkElement: (value): value is ExternalLinkElement =>
        PlateExternalLink.safeParse(value).success,

    isInternalLinkElement: (value): value is InternalLinkElement =>
        PlateInternalLink.safeParse(value).success,

    isTagLinkElement: (value): value is TagLinkElement => PlateTagLink.safeParse(value).success,

    isCategoryLinkElement: (value): value is CategoryLinkElement =>
        PlateCategoryLink.safeParse(value).success,

    isAuthorLinkElement: (value): value is AuthorLinkElement =>
        PlateAuthorLink.safeParse(value).success,

    isOrderedListElement: (value): value is OrderedListElement =>
        PlateOrderedList.safeParse(value).success,

    isUnorderedListElement: (value): value is UnorderedListElement =>
        PlateUnorderedList.safeParse(value).success,

    isListElement: (value): value is ListElement =>
        ElementApi.isOrderedListElement(value) || ElementApi.isUnorderedListElement(value),

    isListItemElement: (value): value is ListItemElement => PlateListItem.safeParse(value).success,

    isNonListableElementType: (node) =>
        isString(node.type) && NON_LISTABLE_ELEMENT_TYPES.has(node.type),

    isAnyCrossheadElement: (value): value is AnyCrossheadElement =>
        PlateAnyCrosshead.safeParse(value).success,

    isAnyCrossheadElementType: (node) =>
        isString(node.type) && ANY_CROSSHEAD_ELEMENT_TYPES.has(node.type),

    isCrossheadDefaultElement: (value): value is CrossheadDefaultElement =>
        PlateCrossheadDefault.safeParse(value).success,

    isCrossheadSubsectionElement: (value): value is CrossheadSubsectionElement =>
        PlateCrossheadSubsection.safeParse(value).success,

    isCrossheadSubsectionElementType: (node) =>
        isString(node.type) && CROSSHEAD_SUBSECTION_ELEMENT_TYPES.has(node.type),

    isCrossheadOrderedListicleElement: (value): value is CrossheadOrderedListicleElement =>
        PlateCrossheadOrderedListicle.safeParse(value).success,

    isCrossheadUnorderedListicleElement: (value): value is CrossheadUnorderedListicleElement =>
        PlateCrossheadUnorderedListicle.safeParse(value).success,

    isFooterElement: (value): value is FooterElement => PlateFooter.safeParse(value).success,

    isFooterElementType: (node) => isString(node.type) && FOOTER_ELEMENT_TYPES.has(node.type),

    isParagraphElement: (value): value is ParagraphElement =>
        PlateParagraph.safeParse(value).success,

    isParagraphElementType: (node) => isString(node.type) && PARAGRAPH_ELEMENT_TYPES.has(node.type),

    isTitleElement: (value): value is TitleElement => PlateTitle.safeParse(value).success,
    isTitleHeaderElement: (value): value is TitleHeaderElement =>
        PlateTitleHeader.safeParse(value).success,
    isLeadElement: (value): value is LeadElement => PlateLead.safeParse(value).success,

    isEmptyParagraphElement: (node) =>
        ElementApi.isParagraphElement(node) && NodeApi.string(node) === '',

    isSlideshowElement: (value): value is SlideshowElement =>
        PlateSlideshow.safeParse(value).success,

    isCsSlideshowElement: (value): value is CsSlideshowElement =>
        PlateCsSlideshow.safeParse(value).success,

    isPollElement: (value): value is PollElement => PlatePoll.safeParse(value).success,

    isPollQuestionElement: (value): value is PollQuestionElement =>
        PlatePollQuestion.safeParse(value).success,

    isPollAnswerElement: (value): value is PollAnswerElement =>
        PlatePollAnswer.safeParse(value).success,

    isRatingPollElement: (value): value is RatingPollElement =>
        PlateRatingPoll.safeParse(value).success,

    isEmptyPollQuestionElement: (value) => {
        if (!ElementApi.isPollQuestionElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isEmptyPollAnswerElement: (value) => {
        if (!ElementApi.isPollAnswerElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isSnippetElement: (value): value is SnippetElement => PlateSnippet.safeParse(value).success,
    isEmbeddedSnippetElement: (value): value is EmbeddedSnippetElement =>
        PlateEmbeddedSnippet.safeParse(value).success,

    isBlueskyElement: (value): value is BlueskyElement => PlateBluesky.safeParse(value).success,
    isFacebookElement: (value): value is FacebookElement => PlateFacebook.safeParse(value).success,
    isTwitterElement: (value): value is TwitterElement => PlateTwitter.safeParse(value).success,
    isYoutubeElement: (value): value is YoutubeElement => PlateYoutube.safeParse(value).success,
    isThreadsElement: (value): value is ThreadsElement => PlateThreads.safeParse(value).success,
    isInstagramElement: (value): value is InstagramElement =>
        PlateInstagram.safeParse(value).success,
    isTiktokElement: (value): value is TiktokElement => PlateTiktok.safeParse(value).success,
    isZattooElement: (value): value is ZattooElement => PlateZattoo.safeParse(value).success,
    isFrontendComponentElement: (value): value is FrontendComponentElement =>
        PlateFrontendComponent.safeParse(value).success,
    isCustomJsonElement: (value): value is CustomJsonElement =>
        PlateCustomJson.safeParse(value).success,

    isSeparatorElement: (value): value is SeparatorElement =>
        PlateSeparator.safeParse(value).success,

    isInterviewSegmentElement: (value): value is InterviewSegmentElement =>
        PlateInterviewSegment.safeParse(value).success,

    isInterviewSegmentElementType: (node) =>
        isString(node.type) && INTERVIEW_SEGMENT_ELEMENT_TYPES.has(node.type),

    isInterviewSegmentQuestionElement: (value): value is InterviewSegmentQuestionElement =>
        PlateInterviewSegmentQuestion.safeParse(value).success,

    isInterviewSegmentAnswerElement: (value): value is InterviewSegmentAnswerElement =>
        PlateInterviewSegmentAnswer.safeParse(value).success,

    isVideoCmsElement: (value): value is VideoCmsElement => PlateVideoCms.safeParse(value).success,

    isEmptyInterviewSegmentQuestionElement: (value) => {
        if (!ElementApi.isInterviewSegmentQuestionElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isEmptyInterviewSegmentAnswerElement: (value) => {
        if (!ElementApi.isInterviewSegmentAnswerElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isEmptyInterviewSegmentElement: (value) => {
        if (!ElementApi.isInterviewSegmentElement(value)) {
            return false;
        }

        // Check if question is empty
        const question = value.children.find((child) =>
            ElementApi.isInterviewSegmentQuestionElement(child),
        );
        if (!question || !ElementApi.isEmptyInterviewSegmentQuestionElement(question)) {
            return false;
        }

        // Check if all answers are empty
        const answers = value.children.filter((child) =>
            ElementApi.isInterviewSegmentAnswerElement(child),
        );
        return answers.every((answer) => ElementApi.isEmptyInterviewSegmentAnswerElement(answer));
    },

    isEmptyInfoboxTitleElement: (value) => {
        if (!ElementApi.isInfoboxTitleElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },
    isEmptyInfoboxContentElement: (value) => {
        if (!ElementApi.isInfoboxContentElement(value)) {
            return false;
        }

        const elements = NodeApi.nodes(value);

        for (const nodeEntry of elements) {
            const [node] = nodeEntry;

            if (ElementApi.isImageElement(node)) {
                return false;
            }
        }
        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isTextElement: (value): value is TextElement => PlateTextElement.safeParse(value).success,

    isEmptyTextElement: (value) => ElementApi.isTextElement(value) && NodeApi.string(value) === '',

    isTextBlockElement: (value): value is TextBlockElement =>
        PlateTextBlockElement.safeParse(value).success,

    isTextBlockElementType: (node) =>
        isString(node.type) && TEXT_BLOCK_ELEMENT_TYPES.has(node.type),

    isTextHeadingElement: (value): value is TitleHeaderElement | TitleElement | LeadElement =>
        ElementApi.isTitleHeaderElement(value) ||
        ElementApi.isTitleElement(value) ||
        ElementApi.isLeadElement(value),

    isTextHeadingElementType: (node) =>
        isString(node.type) && TEXT_HEADING_ELEMENT_TYPES.has(node.type),

    isImageElement: (value): value is ImageElement => PlateImage.safeParse(value).success,
    isEmptyImageElement: (value) => {
        if (!ElementApi.isImageElement(value)) {
            return false;
        }

        return (
            !('data' in value) ||
            (isEmpty(value.data.src?.toString()) && isEmpty(NodeApi.string(value)))
        );
    },

    isCaptionElement: (value): value is CaptionElement => Caption.safeParse(value).success,
    isCreditElement: (value): value is CreditElement => Credit.safeParse(value).success,

    isEmptyCaptionElement: (value) => {
        if (!ElementApi.isCaptionElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isEmptyCreditElement: (value) => {
        if (!ElementApi.isCreditElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isDynamicTeaserElement: (value): value is DynamicTeaserElement =>
        PlateDynamicTeaser.safeParse(value).success,

    isEmptyDynamicTeaserElement: (value) => {
        if (!ElementApi.isDynamicTeaserElement(value)) {
            return false;
        }

        return (
            isEmpty(value.data.metadataIds) &&
            isEmpty(value.data.image?.url?.toString()) &&
            ElementApi.isEmptyDynamicTeaserTitleElement(value.children[0])
        );
    },

    isDynamicTeaserTitleElement: (value): value is DynamicTeaserTitleElement =>
        PlateDynamicTeaserTitle.safeParse(value).success,

    isDynamicTeaserTitleElementType: (node) =>
        isString(node.type) && DYNAMIC_TEASER_TITLE_ELEMENT_TYPES.has(node.type),

    isEmptyDynamicTeaserTitleElement: (value) => {
        if (!ElementApi.isDynamicTeaserTitleElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isQuoteElement: (value): value is QuoteElement => PlateQuote.safeParse(value).success,

    isQuoteTextElement: (value): value is QuoteTextElement =>
        PlateQuoteText.safeParse(value).success,

    isQuoteTextElementType: (node) =>
        isString(node.type) && QUOTE_TEXT_ELEMENT_TYPES.has(node.type),

    isQuoteCaptionElement: (value): value is QuoteCaptionElement =>
        PlateQuoteCaption.safeParse(value).success,

    isQuoteCaptionElementType: (node) =>
        isString(node.type) && QUOTE_CAPTION_ELEMENT_TYPES.has(node.type),

    isEmptyQuoteElement: (value) => {
        if (!ElementApi.isQuoteElement(value)) {
            return false;
        }

        // Check if both quote text and caption are empty
        const quoteText = value.children.find((child) => ElementApi.isQuoteTextElement(child));
        const quoteCaption = value.children.find((child) =>
            ElementApi.isQuoteCaptionElement(child),
        );

        return (
            (!quoteText || ElementApi.isEmptyQuoteTextElement(quoteText)) &&
            (!quoteCaption || ElementApi.isEmptyQuoteCaptionElement(quoteCaption))
        );
    },

    isEmptyQuoteTextElement: (value) => {
        if (!ElementApi.isQuoteTextElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isEmptyQuoteCaptionElement: (value) => {
        if (!ElementApi.isQuoteCaptionElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },

    isInfoboxElement: (value): value is InfoboxElement => PlateInfobox.safeParse(value).success,

    isInfoboxElementType: (node) => isString(node.type) && INFOBOX_ELEMENT_TYPES.has(node.type),

    isInfoboxTitleElement: (value): value is InfoboxTitleElement =>
        PlateInfoboxTitle.safeParse(value).success,
    isInfoboxContentElement: (value): value is InfoboxContentElement =>
        PlateInfoboxContent.safeParse(value).success,

    isInfoboxContentElementType: (node) =>
        isString(node.type) && INFOBOX_CONTENT_ELEMENT_TYPES.has(node.type),

    isInfoboxContentElementChild: (value): value is PlateInfoboxContentElementType =>
        PlateInfoboxContentElement.safeParse(value).success,
    isEmptyInfoboxElement: (value) =>
        ElementApi.isInfoboxElement(value) &&
        ElementApi.isEmptyInfoboxTitleElement(value.children[0]) &&
        ElementApi.isEmptyInfoboxContentElement(value.children[1]),

    isEmbeddedContentElement: (value): value is EmbeddedContentElement =>
        PlateEmbeddedContent.safeParse(value).success,

    isEmptyEmbeddedContentElement: (value) => {
        if (!ElementApi.isEmbeddedContentElement(value)) {
            return false;
        }

        return (
            isEmpty(value.data?.src?.toString()) &&
            ElementApi.isEmptyCaptionElement(value.children[0]) &&
            ElementApi.isEmptyCreditElement(value.children[1])
        );
    },

    isEmptySocialEmbedElement: (value) => {
        if (
            !ElementApi.isBlueskyElement(value) &&
            !ElementApi.isFacebookElement(value) &&
            !ElementApi.isInstagramElement(value) &&
            !ElementApi.isThreadsElement(value) &&
            !ElementApi.isTiktokElement(value) &&
            !ElementApi.isTwitterElement(value) &&
            !ElementApi.isYoutubeElement(value)
        ) {
            return false;
        }

        return isEmpty(value.data?.src?.toString());
    },

    isSummaryListElement: (value): value is SummaryListElement =>
        PlateSummaryList.safeParse(value).success,
    isSummaryListSummaryElement: (value): value is SummaryListSummaryElement =>
        PlateSummaryListSummary.safeParse(value).success,

    isEmptySummaryListSummaryElement: (value): value is SummaryListSummaryElement => {
        if (!ElementApi.isSummaryListSummaryElement(value)) {
            return false;
        }

        const string = NodeApi.string(value);

        return isEmpty(string);
    },
    isEmptySummaryListElement: (value): value is SummaryListElement =>
        ElementApi.isSummaryListElement(value) &&
        ElementApi.isEmptySummaryListSummaryElement(value.children[0]),

    isEmptyElement: (value) =>
        ElementApi.isEmptyTextElement(value) ||
        ElementApi.isEmptyCaptionElement(value) ||
        ElementApi.isEmptyCreditElement(value) ||
        ElementApi.isEmptyImageElement(value) ||
        ElementApi.isEmptyInterviewSegmentElement(value) ||
        ElementApi.isEmptyInfoboxElement(value) ||
        ElementApi.isEmptyEmbeddedContentElement(value) ||
        ElementApi.isEmptySocialEmbedElement(value) ||
        ElementApi.isEmptyQuoteElement(value) ||
        ElementApi.isEmptyDynamicTeaserElement(value) ||
        ElementApi.isEmptySummaryListElement(value),

    isTemplateElement: (value) => Boolean(value?.data?.templateElement),
};
